import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
} from "@mui/material";
import { Student, StudentStatus } from "../students/api";
import { studentStatusApi } from "./studentStatusApi";

interface Props {
  open: boolean;
  onClose: () => void;
  students: Student[];
  onUpdated: () => void;
}

const statusOptions: StudentStatus[] = [
  StudentStatus.ACTIVE,
  StudentStatus.INACTIVE,
  StudentStatus.PASSED_OUT,
  StudentStatus.TC,
  StudentStatus.RE_STUDY,
];

const ChangeStudentStatusDialog: React.FC<Props> = ({
  open,
  onClose,
  students,
  onUpdated,
}) => {
  const [status, setStatus] = useState<StudentStatus | "">("");
  const [reason, setReason] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const selectedCount = students.length;

  const title = useMemo(() => {
    if (selectedCount === 1) {
      const s = students[0];
      const name = `${s.firstName} ${s.lastName || ""}`.trim();
      return `Change Status - ${name}`;
    }
    if (selectedCount > 1) {
      return `Change Status for ${selectedCount} Students`;
    }
    return "Change Student Status";
  }, [selectedCount, students]);

  const handleSubmit = async () => {
    if (!status || students.length === 0) return;
    setSubmitting(true);
    try {
      await studentStatusApi.changeStatusBulk(
        students.map((s) => s.id),
        {
          status,
          reason: reason || undefined,
          effectiveDate: effectiveDate || undefined,
        },
      );
      onUpdated();
      onClose();
    } catch (e) {
      // optional: surface error via snackbar
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label="New Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as StudentStatus)}
            fullWidth
            size="small"
          >
            {statusOptions.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
          <TextField
            label="Effective Date"
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !status}
        >
          Update Status
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeStudentStatusDialog;
